import React from 'react';
import './App.scss';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import {
  AuthProvider,
  useAuth,
} from './component/context/AuthContext/AuthContext';
import { onSignIn, onSignOut } from './utils/authHandlers';
import { AppInsightProvider } from './component/context/AppInsightContext/AppInsightContext';
import { Provider } from 'react-redux';
import store from './store/store';
import SplitProvider from './component/context/SplitContext/SplitProvider';
import { getHarnessTrafficKey } from './utils/harnessTrafficKey';
import { ROUTES } from './constants/routes';
import { useAvailableBetaFeatures } from './beta/useAvailableBetaFeatures';
import BetaLayout from './beta/layout/BetaLayout';
import EntryRoute from './routing/EntryRoute';

const App: React.FC = () => {
  return (
    <div className="App">
      <AuthProvider onSignIn={onSignIn} onSignOut={onSignOut}>
        <BrowserRouter>
          <SplitProvider>
            <AppInsightProvider>
              <Provider store={store}>
                <AppRoutes />
              </Provider>
            </AppInsightProvider>
          </SplitProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, userData } = useAuth();
  const splitKey = getHarnessTrafficKey(userData?.profile);
  const { availableFeatures, isLoading: isBetaLoading } =
    useAvailableBetaFeatures(splitKey);

  if (isLoading || isBetaLoading || !isAuthenticated) {
    return null;
  }

  const betaPath = availableFeatures[0]?.path;

  return (
    <Routes>
      <Route
        path={ROUTES.CLASSIC}
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <EntryRoute betaPath={betaPath} />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.CALLBACK}
        element={<EntryRoute betaPath={betaPath} />}
      />

      {/* Beta UI: only features whose flag is 'on' for this user are routable */}
      <Route
        path={ROUTES.BETA_ROOT}
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            {betaPath ? (
              <BetaLayout features={availableFeatures} />
            ) : (
              <Navigate to={ROUTES.CLASSIC} replace />
            )}
          </ProtectedRoute>
        }
      >
        {betaPath && (
          <>
            <Route index element={<Navigate to={betaPath} replace />} />
            {availableFeatures.map((feature) => (
              <Route
                key={feature.id}
                path={feature.path}
                element={feature.element}
              />
            ))}
            <Route path="*" element={<Navigate to={betaPath} replace />} />
          </>
        )}
      </Route>

      <Route
        path={ROUTES.ROOT}
        element={<Navigate to={ROUTES.CLASSIC} replace />}
      />
      <Route path="*" element={<Navigate to={ROUTES.CLASSIC} replace />} />
    </Routes>
  );
};

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  isAuthenticated: boolean;
}> = ({ children, isAuthenticated }) => {
  if (!isAuthenticated) return null;
  return <>{children}</>;
};

export default App;
